import { create } from 'zustand';
import { auditService } from '../services/auditService';
import type { AuditAction, AuditModule, CreateAuditLogPayload } from '../types/auditLog';

interface AuditStoreState {
  sessionId: string | null;

  startSession: () => void;
  endSession: () => void;

  /**
   * Log an audit event. Automatically fills in sessionId, timestamp, url, userAgent
   * and merges admin info from authStore. Call this for any action not already
   * covered by the Axios interceptor.
   */
  logEvent: (event: {
    action: AuditAction;
    module: AuditModule;
    subModule?: string;
    targetId?: string;
    targetName?: string;
    targetType?: string;
    before?: Record<string, unknown>;
    after?: Record<string, unknown>;
    status?: 'success' | 'failure';
    errorMessage?: string;
  }) => void;
}

function generateSessionId(): string {
  return `sess_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function getAdminContext(): {
  adminId: string;
  adminName: string;
  adminRole: string;
  adminEmail: string;
} {
  // Lazy import to avoid circular dep — read Zustand state directly
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { useAuthStore } = require('../store/authStore');
  const state = useAuthStore.getState();
  const user = state.user;
  const fullName =
    user?.name ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    'Unknown';
  return {
    adminId: user?.id ?? state.staffUserId ?? 'unknown',
    adminName: fullName,
    adminRole: state.role ?? 'unknown',
    adminEmail: user?.email ?? 'unknown',
  };
}

export const useAuditStore = create<AuditStoreState>((set, get) => ({
  sessionId: null,

  startSession: () => {
    set({ sessionId: generateSessionId() });
  },

  endSession: () => {
    set({ sessionId: null });
  },

  logEvent: (event) => {
    const sessionId = get().sessionId ?? generateSessionId();
    const admin = getAdminContext();

    const payload: CreateAuditLogPayload = {
      ...admin,
      sessionId,
      timestamp: new Date().toISOString(),
      url: window.location.pathname + window.location.search,
      userAgent: navigator.userAgent,
      status: 'success',
      ...event,
    };

    // Fire and forget — never block the calling code
    auditService.create(payload).catch(() => undefined);
  },
}));
